import { Box, Divider, Typography } from '@mui/material'
import type { ReactNode } from 'react'
import { renderLegalInline } from '@/lib/legal/renderLegalInline'

export default function LegalMarkdownView({ markdown }: { markdown: string }) {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n')
  const blocks: ReactNode[] = []
  let index = 0

  while (index < lines.length) {
    const line = lines[index]
    const trimmed = line.trim()

    if (!trimmed) {
      index += 1
      continue
    }

    if (trimmed === '---') {
      blocks.push(<Divider key={`hr-${index}`} sx={{ my: 2 }} />)
      index += 1
      continue
    }

    if (trimmed.startsWith('# ')) {
      blocks.push(
        <Typography key={`h1-${index}`} variant="h4" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
          {trimmed.slice(2)}
        </Typography>,
      )
      index += 1
      continue
    }

    if (trimmed.startsWith('## ')) {
      blocks.push(
        <Typography key={`h2-${index}`} variant="h5" component="h2" sx={{ fontWeight: 700, mt: 3, mb: 1.5 }}>
          {trimmed.slice(3)}
        </Typography>,
      )
      index += 1
      continue
    }

    if (trimmed.startsWith('### ')) {
      blocks.push(
        <Typography key={`h3-${index}`} variant="h6" component="h3" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
          {trimmed.slice(4)}
        </Typography>,
      )
      index += 1
      continue
    }

    if (trimmed.startsWith('> ')) {
      const quoteLines: string[] = []
      while (index < lines.length && lines[index].trim().startsWith('> ')) {
        quoteLines.push(lines[index].trim().slice(2))
        index += 1
      }
      blocks.push(
        <Box
          key={`quote-${index}`}
          sx={{
            borderLeft: '4px solid',
            borderColor: 'primary.main',
            pl: 2,
            py: 1,
            mb: 2,
            bgcolor: 'action.hover',
            borderRadius: 1,
          }}
        >
          <Typography variant="body2">{renderLegalInline(quoteLines.join(' '))}</Typography>
        </Box>,
      )
      continue
    }

    if (trimmed.startsWith('|')) {
      const tableLines: string[] = []
      while (index < lines.length && lines[index].trim().startsWith('|')) {
        tableLines.push(lines[index].trim())
        index += 1
      }
      const rows = tableLines
        .filter((row) => !row.includes('---'))
        .map((row) =>
          row
            .split('|')
            .slice(1, -1)
            .map((cell) => cell.trim()),
        )
      blocks.push(
        <Box key={`table-${index}`} sx={{ overflowX: 'auto', mb: 2 }}>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <Box component="tbody">
              {rows.map((row, rowIndex) => (
                <Box component="tr" key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <Box
                      component={rowIndex === 0 ? 'th' : 'td'}
                      key={cellIndex}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        p: 1,
                        textAlign: 'left',
                        fontWeight: rowIndex === 0 ? 700 : 400,
                        verticalAlign: 'top',
                      }}
                    >
                      {renderLegalInline(cell)}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>,
      )
      continue
    }

    if (trimmed.startsWith('- ')) {
      const items: string[] = []
      while (index < lines.length && lines[index].trim().startsWith('- ')) {
        items.push(lines[index].trim().slice(2))
        index += 1
      }
      blocks.push(
        <Box component="ul" key={`ul-${index}`} sx={{ pl: 3, mb: 2 }}>
          {items.map((item, itemIndex) => (
            <Box component="li" key={itemIndex} sx={{ mb: 0.5 }}>
              <Typography variant="body2" component="span" sx={{ display: 'inline' }}>
                {renderLegalInline(item)}
              </Typography>
            </Box>
          ))}
        </Box>,
      )
      continue
    }

    const paragraphLines: string[] = [trimmed]
    index += 1
    while (index < lines.length) {
      const next = lines[index].trim()
      if (
        !next ||
        next.startsWith('#') ||
        next.startsWith('>') ||
        next.startsWith('|') ||
        next.startsWith('- ') ||
        next === '---'
      ) {
        break
      }
      paragraphLines.push(next)
      index += 1
    }

    blocks.push(
      <Typography key={`p-${index}`} variant="body2" sx={{ mb: 1.5, lineHeight: 1.7 }}>
        {renderLegalInline(paragraphLines.join(' '))}
      </Typography>,
    )
  }

  return <Box sx={{ maxWidth: 900, mx: 'auto' }}>{blocks}</Box>
}
